/**
 * Song-level performance metadata: a tempo (quarter-note BPM) and a global
 * time signature on a song (V3 or V4; both carry them the same way).
 *
 * Both are optional on `ChordCakeStateV3` / `ChordCakeStateV4` (`tempo?: number | null`,
 * `timeSignature?: TimeSignatureV3 | null`); absent and null both mean "not
 * set". Every consumer (apps, PDF, Johan, Buffet) reads them through
 * `getSongTempo` / `getSongTimeSignature` so the validity rules live here.
 *
 * The global time signature and the per-row ones (`RowStateV3.timeSignature`)
 * are mutually exclusive: setting a global one clears the rows, and while it
 * is set the row values are ignored (see `getEffectiveRowTimeSignature`).
 */
/** Slowest tempo the editors offer and the normalizer keeps. */
export const TEMPO_MIN = 20;
/** Fastest tempo the editors offer and the normalizer keeps. */
export const TEMPO_MAX = 300;
/**
 * Valid denominators for a time signature: powers of two from 1 to 32.
 * Covers everything from whole-note (1) to 32nd-note (32) pulses.
 */
export const VALID_TIME_SIGNATURE_DENOMINATORS = [1, 2, 4, 8, 16, 32];
/**
 * Normalize a raw tempo: non-numbers and values at or below zero become
 * null, fractions round, and the rest clamps into TEMPO_MIN..TEMPO_MAX.
 * Never throws, so a hand-edited file can't fail to import over it.
 */
export function normalizeSongTempo(value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        return null;
    }
    return Math.min(Math.max(Math.round(value), TEMPO_MIN), TEMPO_MAX);
}
/** True when the value is a structurally valid time signature. */
export function isValidTimeSignature(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const { numerator, denominator } = value;
    return (typeof numerator === 'number' &&
        Number.isInteger(numerator) &&
        numerator >= 1 &&
        typeof denominator === 'number' &&
        VALID_TIME_SIGNATURE_DENOMINATORS.includes(denominator));
}
/**
 * Normalize a raw time signature to a fresh `{numerator, denominator}` or
 * null when it isn't valid. Never throws.
 */
export function normalizeSongTimeSignature(value) {
    if (!isValidTimeSignature(value)) {
        return null;
    }
    return { numerator: value.numerator, denominator: value.denominator };
}
/** A song with neither setting. */
export const EMPTY_SONG_META = Object.freeze({ tempo: null, timeSignature: null });
/** Reads both settings off any song-shaped object. */
export function readSongMeta(song) {
    return { tempo: getSongTempo(song), timeSignature: getSongTimeSignature(song) };
}
/**
 * The fields to spread into a saved/exported song: only the settings that
 * are set, so a song without them keeps no extra keys.
 */
export function songMetaFields(meta) {
    return {
        ...(meta.tempo !== null && { tempo: meta.tempo }),
        ...(meta.timeSignature !== null && { timeSignature: meta.timeSignature }),
    };
}
/** The song's tempo when one is set (TEMPO_MIN..TEMPO_MAX), else null. */
export function getSongTempo(song) {
    return normalizeSongTempo(song.tempo);
}
/** The song's global time signature when one is set, else null. */
export function getSongTimeSignature(song) {
    return normalizeSongTimeSignature(song.timeSignature);
}
/**
 * The time signature to show on a row: none while a global one is set
 * (the global value replaces the per-row ones), else the row's own value.
 */
export function getEffectiveRowTimeSignature(globalTimeSignature, row) {
    if (globalTimeSignature) {
        return null;
    }
    return row.timeSignature ?? null;
}
/** How many rows carry their own time signature. */
export function countRowTimeSignatures(sections) {
    let count = 0;
    for (const section of sections) {
        for (const row of section.rows) {
            if (row.timeSignature) {
                count++;
            }
        }
    }
    return count;
}
/**
 * The same sections with every row's time signature removed. Returns the
 * input array untouched when no row has one, so callers can skip a no-op
 * state update.
 */
export function clearRowTimeSignatures(sections) {
    if (countRowTimeSignatures(sections) === 0) {
        return sections;
    }
    return sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => (row.timeSignature ? { ...row, timeSignature: null } : row)),
    }));
}
/** Display text for a time signature: "4/4". Never translated. */
export function formatTimeSignature(timeSignature) {
    return `${timeSignature.numerator}/${timeSignature.denominator}`;
}
/** Display text for a tempo: "♩ 120" (the note as its icon, like the other chips). Never translated. */
export function formatTempo(tempo) {
    return `♩ ${tempo}`;
}
